import type { ReactNode } from 'react';
import { HelpTooltip } from '../../shared/components/HelpTooltip';
import { JurisdictionBadge, StatusChip } from './JurisdictionBadge';
import type { JurisdictionEntry, RequirementStatus } from './types';

interface Props {
  label: string;
  /** The value text (a jurisdiction name, or a fact such as "Assigned"). Null shows `emptyText`. */
  text: string | null;
  emptyText?: string;
  /** The catalog entry for the value, when it is a jurisdiction: shows its availability badge. */
  entry?: JurisdictionEntry | undefined;
  /** Where the value comes from, or why it is missing. Never claims an automatic determination that did not happen. */
  source?: string | null | undefined;
  /** A status shown beside the value, for example Needs review for the local jurisdictions. */
  chip?: RequirementStatus | undefined;
  /** Help text for the "i" button beside the label. */
  help?: string | undefined;
  testId?: string;
  children?: ReactNode;
}

/** A read-only tax fact: the label, the value (with its availability badge when it is a jurisdiction), and a source line. Never an input. */
export function JurisdictionFact({ label, text, emptyText = 'Not available yet', entry, source, chip, help, testId, children }: Props) {
  return (
    <div className="ts ts-fact">
      <span className="ts-fact__label">
        {label}
        {help && <HelpTooltip label={`About ${label}`} text={help} />}
      </span>
      <span className={text ? 'ts-fact__value' : 'ts-fact__value ts-fact__value--empty'} data-testid={testId} data-status={text ? 'Verified' : 'Unknown'}>
        {text ?? emptyText}
        {text && entry && <JurisdictionBadge availability={entry.availability} />}
        {chip && <StatusChip status={chip} />}
      </span>
      {source && <span className="ts-fact__source">{source}</span>}
      {children}
    </div>
  );
}
